import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Quote from '../components/Quote';
import { SiteContent } from '../content';
import { ScarabIcon } from '../constants';

const Home: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
        }
      });
    }, { threshold: 0.01, rootMargin: "0px 0px -100px 0px" });

    const elements = containerRef.current?.querySelectorAll('.animate-on-scroll');
    elements?.forEach(el => observer.observe(el));

    // Force visibility fallback
    const timeoutId = setTimeout(() => {
      elements?.forEach(el => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight) el.classList.add('is-visible');
      });
    }, 800);

    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
    };
  }, []);

  const { hero, invitation, closing } = SiteContent.home;

  return (
    <div ref={containerRef} className="flex flex-col w-full bg-paper overflow-hidden">
      {/* SECTION 1: HERO - Editorial Asymmetry */}
      <section className="relative min-h-screen w-full flex items-center justify-start overflow-hidden px-6 md:px-12 lg:px-24 bg-rich-black">
        <img 
          src="https://images.unsplash.com/photo-1592919016322-50f094b97ef0?auto=format&fit=crop&q=80&w=2400" 
          alt="Soil Ground" 
          className="absolute inset-0 w-full h-full object-cover opacity-60 mix-blend-overlay"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/40 to-transparent" />
        
        <div className="relative z-10 max-w-7xl w-full grid md:grid-cols-12 gap-12 pt-20">
          <div className="md:col-span-8">
            <span className="editorial-label opacity-0 animate-[reveal_1.2s_ease_0.2s_forwards] text-white">01. THE AWAKENING</span>
            <h1 className="hero-title mb-10 opacity-0 animate-[reveal_1.2s_ease_0.4s_forwards]">
              A Seat for the <span className="serif-italic lowercase tracking-normal">Soil</span> at the Big Business Table
            </h1>
            <p className="serif italic text-[#F5F0E6] text-xl md:text-3xl mb-12 max-w-2xl opacity-0 animate-[reveal_1.2s_ease_0.6s_forwards]">
              "{hero.subheadline}"
            </p>
          </div>
          <div className="md:col-span-4 flex flex-col justify-end pb-12">
             <div className="opacity-0 animate-[reveal_1.2s_ease_0.8s_forwards] border-l border-[#A65D3F] pl-6">
                <p className="text-[#F5F0E6]/70 text-sm md:text-base mb-8 leading-relaxed font-medium">
                  {hero.description}
                </p>
                <Link to="/shop" className="btn-primary">
                  {SiteContent.common.shopAlphabet}
                </Link>
             </div>
          </div>
        </div>

        <div className="vertical-text right-12 top-1/2 -translate-y-1/2 hidden lg:block !text-white/10 uppercase">
          REREGENERATIVE FUTURES — BENEATH THE FEET
        </div>
      </section>

      {/* SECTION 2: THE MANIFESTO */}
      <section className="section-padding relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-24 items-start">
            <div className="lg:col-span-6 z-10">
              <span className="editorial-label">THE MANIFESTO</span>
              <div className="animate-on-scroll animate-fade-up">
                <h2 className="section-title mb-12">
                  Art That Speaks for the <span className="serif-italic lowercase tracking-normal">Soil</span>
                </h2>
              </div>
              <div className="animate-on-scroll animate-fade-up delay-200">
                <p className="text-xl md:text-2xl font-medium text-[#1A1A1A]/85 leading-relaxed mb-12">
                  ERB creates collectible alphabet art—digital and physical—that does more than decorate. Each piece you own is a voice in a growing movement to regenerate Earth's soil.
                </p>
                <ul className="space-y-6 mb-12">
                   {['Join a movement funding soil regeneration', 'Own first-edition collectible art', 'Become part of the founding community', 'Support regenerative certification'].map((item, i) => (
                     <li key={i} className="flex items-center space-x-4 group">
                        <span className="w-2 h-2 bg-[#A65D3F] rotate-45 group-hover:scale-125 transition-transform"></span>
                        <span className="font-bold text-xs tracking-widest uppercase opacity-60 group-hover:opacity-100 transition-opacity">{item}</span>
                     </li>
                   ))}
                </ul>
              </div>
            </div>
            
            <div className="lg:col-span-6 relative">
              <div className="animate-on-scroll animate-mask aspect-[4/5] rounded-lg overflow-hidden shadow-2xl img-placeholder">
                 <img 
                    src="https://images.unsplash.com/photo-1544377193-33dcf4d68fb5?auto=format&fit=crop&q=80&w=1200" 
                    alt="Problem shift" 
                    className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-1000" 
                 />
              </div>
              <div className="absolute -bottom-10 -left-10 wax-seal w-32 h-32 rounded-full flex items-center justify-center animate-on-scroll animate-scale-in delay-500 z-20">
                 <ScarabIcon className="text-white/40 w-16 h-16" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: THE CYCLE */}
      <section className="min-h-[70vh] flex items-center bg-rich-black relative py-24">
        <div className="max-w-7xl mx-auto px-6">
           <div className="text-center mb-24">
              <span className="editorial-label !text-white/30">THE CYCLE</span>
              <div className="animate-on-scroll animate-fade-up">
                 <h2 className="hero-title !text-white mb-8">{invitation.title}</h2>
              </div>
           </div>

           <div className="grid md:grid-cols-3 gap-16">
              {['YOU COLLECT', 'THE MOVEMENT GROWS', 'SOIL REGENERATES'].map((step, i) => (
                <div key={i} className="animate-on-scroll animate-fade-up" style={{ transitionDelay: `${i * 200}ms` }}>
                   <div className="text-5xl font-black text-[#A65D3F] mb-6 opacity-30">0{i+1}</div>
                   <h4 className="font-condensed text-white text-xl font-black mb-4 tracking-widest">{step}</h4>
                   <p className="text-white/40 text-xs leading-relaxed uppercase tracking-widest">
                      {i === 0 && "Purchase alphabet art. Each piece is your stake in the movement."}
                      {i === 1 && "More collectors = stronger voice = greater impact."}
                      {i === 2 && "Resources flow to regenerative farms and certified businesses."}
                   </p>
                </div>
              ))}
           </div>

           <div className="mt-40 text-center animate-on-scroll animate-fade-up delay-500">
              <Quote large text={invitation.quote} />
           </div>
        </div>
      </section>

      {/* SECTION 4: ROADMAP */}
      <section className="section-padding bg-paper">
        <div className="max-w-7xl mx-auto px-6">
           <div className="flex flex-col md:flex-row justify-between items-end mb-24 border-b border-black/5 pb-12">
              <div className="max-w-2xl">
                 <span className="editorial-label">ROADMAP</span>
                 <h2 className="section-title">Where We're <span className="serif-italic lowercase tracking-normal">Going</span></h2>
              </div>
              <Link to="/goals" className="text-[10px] font-black uppercase tracking-[0.5em] border-b-2 border-[#A65D3F] pb-2 mb-2 hover:text-[#A65D3F] transition-colors">
                SEE THE FULL VISION
              </Link>
           </div>

           <div className="grid md:grid-cols-3 gap-8">
              {['BUILD THE MOVEMENT', 'CREATE CERTIFICATION', 'SUPPORT & LOOP'].map((phase, i) => (
                <div key={i} className="group p-10 bg-white/50 border border-black/5 hover:bg-white transition-all duration-700 animate-on-scroll animate-scale-in" style={{ transitionDelay: `${i * 100}ms` }}>
                   <span className="text-[10px] font-black tracking-widest opacity-20 group-hover:text-[#A65D3F] transition-colors">PHASE 0{i+1}</span>
                   <h4 className="font-condensed text-2xl font-black my-4 tracking-wider">{phase}</h4>
                   <p className="text-xs text-black/50 leading-relaxed uppercase tracking-widest font-medium">
                      {i === 0 && "Grow the community of believers through art, message, and shared values."}
                      {i === 1 && "Develop a certification system for businesses committed to regenerating soil."}
                      {i === 2 && "Fund certified businesses. Create economic loops that sustain the mission forever."}
                   </p>
                </div>
              ))}
           </div>
        </div>
      </section>

      {/* SECTION 5: CLOSING */}
      <section className="py-40 md:py-64 bg-rich-black text-center relative overflow-hidden">
        <div className="vertical-text left-12 top-1/2 -translate-y-1/2 !text-white/5">
           STAKE YOUR CLAIM — REPRESENT THE EARTH
        </div>
        
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <div className="animate-on-scroll animate-fade-up">
             <Quote large text={closing.quote} className="mb-20" />
             <p className="text-white/40 text-lg md:text-xl italic mb-16">
               {closing.description}
             </p>
          </div>
          <div className="pt-8 animate-on-scroll animate-scale-in delay-200">
            <Link to="/shop" className="btn-primary !px-16 !py-6 !text-sm">
              SHOP NOW
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;